import React from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

import {
  emptyLedger,
  reduceHistoryLedger,
  type HistoryLocationSnapshot,
  type NavigationHistoryContextValue,
} from './historyLedger';

const NavigationHistoryContext = React.createContext<NavigationHistoryContextValue | null>(null);

function NavigationHistoryProviderInner({
  scopeId,
  children,
}: {
  scopeId: string;
  children: React.ReactNode;
}) {
  const routerLocation = useLocation();
  const action = useNavigationType();
  const location = React.useMemo<HistoryLocationSnapshot>(() => ({
    key: routerLocation.key,
    pathname: routerLocation.pathname,
    search: routerLocation.search,
    hash: routerLocation.hash,
  }), [
    routerLocation.hash,
    routerLocation.key,
    routerLocation.pathname,
    routerLocation.search,
  ]);
  const [ledger, dispatch] = React.useReducer(
    reduceHistoryLedger,
    scopeId,
    emptyLedger,
  );
  const transition = React.useMemo(() => ({ action, location }), [action, location]);
  const publishedLedger = React.useMemo(
    () => reduceHistoryLedger(ledger, transition),
    [ledger, transition],
  );

  React.useLayoutEffect(() => {
    dispatch(transition);
  }, [transition]);

  const value = React.useMemo<NavigationHistoryContextValue>(() => ({
    ...publishedLedger,
    location,
    action,
  }), [action, location, publishedLedger]);

  return (
    <NavigationHistoryContext.Provider value={value}>
      {children}
    </NavigationHistoryContext.Provider>
  );
}

export function NavigationHistoryProvider({
  scopeId,
  children,
}: {
  scopeId: string;
  children: React.ReactNode;
}) {
  return (
    <NavigationHistoryProviderInner key={scopeId} scopeId={scopeId}>
      {children}
    </NavigationHistoryProviderInner>
  );
}

// Keep the hook beside its private context so consumers cannot import the context directly.
// eslint-disable-next-line react-refresh/only-export-components
export function useNavigationHistory(): NavigationHistoryContextValue {
  const value = React.useContext(NavigationHistoryContext);
  if (!value) {
    throw new Error('useNavigationHistory must be used within NavigationHistoryProvider');
  }
  return value;
}

// Query codecs can also be rendered in isolation (for example, composition
// probes). Scroll continuation is opportunistic there and must not make the
// query API require the full application shell.
// eslint-disable-next-line react-refresh/only-export-components
export function useOptionalNavigationHistory(): NavigationHistoryContextValue | null {
  return React.useContext(NavigationHistoryContext);
}
